// src/pages/NutritionDiary.tsx
import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Select, Input, Table, Space, Typography, Modal, Row, Col, InputNumber } from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import { diaryAPI, foodAPI } from '../utils/api';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { showToast } from '../utils/notifications';
import dayjs from 'dayjs';
import { useIsMobile } from '../hooks/useIsMobile';
import { formatDiaryDateTime, sortDiaryEntriesNewestFirst } from '../utils/diaryDisplay';

const { Title } = Typography;

export const NutritionDiary: React.FC = () => {
  const isMobile = useIsMobile();
  const [form] = Form.useForm();
  const [entries, setEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingEntry, setEditingEntry] = useState<any>(null);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFoods, setSelectedFoods] = useState<any[]>([]);
  const [totalNutrition, setTotalNutrition] = useState({ calories: 0, protein: 0, fat: 0, carbs: 0 });
  const [customFoodOpen, setCustomFoodOpen] = useState(false);
  const [customFoodLoading, setCustomFoodLoading] = useState(false);
  const [customFoodForm] = Form.useForm();
  const searchTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const searchInputRef = React.useRef<any>(null);

  const mealTypes = [
    { label: 'Завтрак', value: 'breakfast' },
    { label: 'Обед', value: 'lunch' },
    { label: 'Ужин', value: 'dinner' },
    { label: 'Перекус', value: 'snack' },
  ];

  useEffect(() => {
    loadEntries();
  }, []);

  const loadEntries = async () => {
    try {
      setLoading(true);
      const response = await diaryAPI.getMealEntries();
      const entriesData = Array.isArray(response.data) ? response.data : [];
      setEntries(sortDiaryEntriesNewestFirst(entriesData));
    } catch (error) {
      console.error('Error loading meal entries:', error);
      showToast.error('Ошибка загрузки записей питания');
    } finally {
      setLoading(false);
    }
  };

  // Search for food items with debounce
  const searchFoods = async (value: string) => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (value.length < 1) {
      setSearchResults([]);
      return;
    }

    // Debounce search - wait 300ms after user stops typing
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await foodAPI.searchFoods(value);
        setSearchResults(response.data as any[]);
      } catch (error) {
        console.error('Error searching foods:', error);
        setSearchResults([]);
      }
    }, 300);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Keep focus on input when search results update
  useEffect(() => {
    if (searchInputRef.current && searchResults.length > 0) {
      // Use setTimeout to ensure focus happens after render
      setTimeout(() => {
        if (searchInputRef.current && document.activeElement !== searchInputRef.current.input) {
          searchInputRef.current.focus();
        }
      }, 0);
    }
  }, [searchResults]);

  // Add food to selected list
  const addFood = (food: any) => {
    const existingFood = selectedFoods.find(f => f.id === food.id);
    if (existingFood) {
      showToast.warning('Этот продукт уже добавлен');
      return;
    }
    
    const newFood = { ...food, quantity: 100 }; // Default 100g
    setSelectedFoods([...selectedFoods, newFood]);
    calculateTotalNutrition([...selectedFoods, newFood]);
    setSearchTerm(''); // Clear search term after adding
    setSearchResults([]); // Clear search results
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setSearchResults([]);
  };

  const submitCustomFood = async (values: any, force = false) => {
    try {
      setCustomFoodLoading(true);
      const response = await foodAPI.createCustomFood(
        {
          name: values.name,
          calories_per_100g: values.calories_per_100g,
          protein_per_100g: values.protein_per_100g ?? 0,
          fat_per_100g: values.fat_per_100g ?? 0,
          carbs_per_100g: values.carbs_per_100g ?? 0,
        },
        force
      );
      const data = response.data as any;
      if (data?.needs_confirmation) {
        Modal.confirm({
          title: 'Похожий продукт уже есть',
          content: (data.similar || [])
            .map((s: any) => `${s.display_name} (${s.calories_per_100g} ккал)`)
            .join(', '),
          okText: 'Всё равно создать',
          cancelText: 'Отмена',
          onOk: () => submitCustomFood(values, true),
        });
        return;
      }
      showToast.success('Продукт сохранён в вашем списке');
      setCustomFoodOpen(false);
      customFoodForm.resetFields();
      addFood(data);
    } catch (error: any) {
      showToast.error(error.response?.data?.detail || 'Не удалось создать продукт');
    } finally {
      setCustomFoodLoading(false);
    }
  };

  // Remove food from selected list
  const removeFood = (id: number) => {
    const updatedFoods = selectedFoods.filter(f => f.id !== id);
    setSelectedFoods(updatedFoods);
    calculateTotalNutrition(updatedFoods);
  };

  // Update food quantity
  const updateFoodQuantity = (id: number, quantity: number) => {
    const updatedFoods = selectedFoods.map(f => 
      f.id === id ? { ...f, quantity } : f
    );
    setSelectedFoods(updatedFoods);
    calculateTotalNutrition(updatedFoods);
  };

  // Calculate total nutrition based on selected foods
  const calculateTotalNutrition = (foods: any[]) => {
    const total = foods.reduce((acc, food) => {
      const multiplier = food.quantity / 100;
      return {
        calories: acc.calories + (food.calories_per_100g * multiplier),
        protein: acc.protein + (food.protein_per_100g * multiplier),
        fat: acc.fat + (food.fat_per_100g * multiplier),
        carbs: acc.carbs + (food.carbs_per_100g * multiplier)
      };
    }, { calories: 0, protein: 0, fat: 0, carbs: 0 });
    
    setTotalNutrition(total);
  };

  const onFinish = async (values: any) => {
    try {
      if (selectedFoods.length === 0) {
        showToast.error('Добавьте хотя бы один продукт');
        return;
      }

      const data = {
        meal_type: values.meal_type,
        foods: selectedFoods.map(food => ({
          food_id: food.id,
          quantity: food.quantity
        })),
        timestamp: editingEntry?.timestamp || new Date().toISOString()
      };
      
      if (editingEntry) {
        // Update existing entry
        await diaryAPI.updateMealEntry(editingEntry.id, data);
        showToast.success('Прием пищи успешно обновлен!');
      } else {
        // Create new entry
        await diaryAPI.createMealEntry(data);
        showToast.success('Прием пищи успешно добавлен!');
      }
      
      form.resetFields();
      setSelectedFoods([]);
      setTotalNutrition({ calories: 0, protein: 0, fat: 0, carbs: 0 });
      setSearchTerm('');
      setSearchResults([]);
      await loadEntries(); // Reload entries after successful save
      setModalVisible(false);
      setEditingEntry(null);
    } catch (error: any) {
      console.error('Error saving meal entry:', error);
      showToast.error(error.response?.data?.detail || 'Ошибка сохранения приема пищи');
    }
  };

  const handleEdit = async (record: any) => {
    try {
      setLoading(true);
      // Load full meal entry details
      const response = await diaryAPI.getMealEntry(record.id);
      const mealEntry = response.data as any;
      
      setEditingEntry(mealEntry);
      form.setFieldsValue({
        meal_type: mealEntry.meal_type,
      });
      
      // Parse foods JSON and load full food item data
      let foodsData: any[] = [];
      try {
        const foodsJson = typeof mealEntry.foods === 'string' ? JSON.parse(mealEntry.foods) : mealEntry.foods;
        foodsData = foodsJson.map((foodItem: any) => ({
          id: foodItem.id,
          name: foodItem.name,
          name_ru: foodItem.name_ru || foodItem.name,
          calories_per_100g: (foodItem.calories / foodItem.quantity) * 100,
          protein_per_100g: (foodItem.protein / foodItem.quantity) * 100,
          fat_per_100g: (foodItem.fat / foodItem.quantity) * 100,
          carbs_per_100g: (foodItem.carbs / foodItem.quantity) * 100,
          quantity: foodItem.quantity
        }));
      } catch (e) {
        console.error('Error parsing foods:', e);
          showToast.error('Ошибка загрузки продуктов из приема пищи');
      }
      
      setSelectedFoods(foodsData);
      calculateTotalNutrition(foodsData);
      setModalVisible(true);
    } catch (error: any) {
      console.error('Error loading meal entry:', error);
      showToast.error('Ошибка загрузки приема пищи');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Прием пищи',
      dataIndex: 'meal_type',
      key: 'meal_type',
      render: (value: string) => mealTypes.find(m => m.value === value)?.label || value,
    },
    {
      title: 'Калории',
      dataIndex: 'total_calories',
      key: 'total_calories',
      render: (value: number) => `${Math.round(value)} ккал`,
    },
    {
      title: 'Белки',
      dataIndex: 'total_protein',
      key: 'total_protein',
      render: (value: number) => `${Math.round(value)} г`,
    },
    {
      title: 'Жиры',
      dataIndex: 'total_fat',
      key: 'total_fat',
      render: (value: number) => `${Math.round(value)} г`,
    },
    {
      title: 'Углеводы',
      dataIndex: 'total_carbs',
      key: 'total_carbs',
      render: (value: number) => `${Math.round(value)} г`,
    },
    {
      title: 'Дата и время',
      key: 'datetime',
      render: (_: any, record: any) => formatDiaryDateTime(record),
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} size="small" />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: isMobile ? '0' : '0' }}>
      <div style={{ marginBottom: isMobile ? 16 : 24 }}>
        <Title level={isMobile ? 3 : 2} style={{ color: '#263238', fontSize: isMobile ? '20px' : '24px' }}>Дневник питания</Title>
        <Typography.Text type="secondary" style={{ fontSize: isMobile ? '13px' : '14px' }}>
          Записывайте приемы пищи и отслеживайте питательную ценность
        </Typography.Text>
      </div>

      <Card 
        title="Добавить новый прием пищи" 
        style={{ marginBottom: isMobile ? 16 : 24, borderRadius: '12px' }}
        headStyle={{ fontSize: isMobile ? '16px' : '18px', padding: isMobile ? '12px 16px' : '16px 24px' }}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Row gutter={isMobile ? 8 : 16}>
            <Col span={24} md={8}>
              <Form.Item
                label="Прием пищи"
                name="meal_type"
                rules={[{ required: true, message: 'Выберите прием пищи!' }]}
              >
                <Select 
                  placeholder="Выберите прием пищи" 
                  options={mealTypes} 
                  size="large"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label={
              <Space>
                <span>Поиск продуктов</span>
                <Button type="link" size="small" onClick={() => setCustomFoodOpen(true)} style={{ padding: 0 }}>
                  + Свой продукт
                </Button>
              </Space>
            }
            style={{ position: 'relative' }}
          >
            <Input
              ref={searchInputRef}
              placeholder="Например: хлеб, творог, гречка..."
              value={searchTerm}
              onChange={(e) => {
                const value = e.target.value;
                setSearchTerm(value);
                searchFoods(value);
              }}
              onBlur={(e) => {
                // Don't blur if clicking on search results
                const relatedTarget = e.relatedTarget as HTMLElement;
                if (relatedTarget && relatedTarget.closest('.search-results-dropdown')) {
                  e.preventDefault();
                  searchInputRef.current?.focus();
                }
              }}
              size="large"
              suffix={searchTerm && (
                <Button 
                  type="text" 
                  onClick={(e) => {
                    e.stopPropagation();
                    setSearchTerm('');
                    setSearchResults([]);
                    if (searchTimeoutRef.current) {
                      clearTimeout(searchTimeoutRef.current);
                    }
                  }}
                >
                  ✕
                </Button>
              )}
            />
            
            {/* Search results dropdown */}
            {searchResults.length > 0 && (
              <div 
                className="search-results-dropdown"
                style={{ 
                  position: 'absolute', 
                  zIndex: 1000, 
                  width: '100%', 
                  backgroundColor: 'white', 
                  border: '1px solid #d9d9d9', 
                  borderRadius: '6px',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  marginTop: '4px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }}
                onMouseDown={(e) => {
                  // Prevent input blur when clicking on dropdown
                  e.preventDefault();
                }}
              >
                {searchResults.map(food => (
                  <div
                    key={food.id}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f0f0f0',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'white';
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      addFood(food);
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault(); // Prevent input blur
                    }}
                  >
                    <div style={{ fontWeight: 500 }}>{food.display_name || food.name_ru || food.name}</div>
                    <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '2px' }}>
                      {food.category || food.food_category || 'Продукт'} · {food.calories_per_100g} ккал/100г · Б {food.protein_per_100g} / Ж {food.fat_per_100g} / У {food.carbs_per_100g}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Form.Item>

          {/* Selected foods list */}
          {selectedFoods.length > 0 && (
            <Card 
              title="Выбранные продукты" 
              size="small" 
              style={{ marginBottom: 16, borderRadius: '8px' }}
            >
              {selectedFoods.map(food => (
                <div key={food.id} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '8px 0',
                  borderBottom: '1px solid #f0f0f0'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500 }}>{food.display_name || food.name_ru || food.name}</div>
                    <div style={{ fontSize: '12px', color: '#595959' }}>
                      {food.calories_per_100g} ккал/100г
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <InputNumber
                      value={food.quantity}
                      onChange={(value) => updateFoodQuantity(food.id, value ?? 0)}
                      min={0}
                      max={10000}
                      step={10}
                      size="small"
                      style={{ width: 100 }}
                      addonAfter="г"
                      parser={(value) => value!.replace(/\D/g, '')}
                      formatter={(value) => value ? `${value}` : ''}
                    />
                    <Button 
                      size="small" 
                      danger 
                      onClick={() => removeFood(food.id)}
                    >
                      Удалить
                    </Button>
                  </div>
                </div>
              ))}
            </Card>
          )}

          {/* Total nutrition summary */}
          {selectedFoods.length > 0 && (
            <Card 
              title="Итоговая питательная ценность" 
              size="small" 
              style={{ marginBottom: 16, borderRadius: '8px' }}
            >
              <Row gutter={16}>
                <Col span={6}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#7CB342' }}>
                      {Math.round(totalNutrition.calories)}
                    </div>
                    <div style={{ fontSize: '12px', color: '#595959' }}>ккал</div>
                  </div>
                </Col>
                <Col span={6}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#7CB342' }}>
                      {Math.round(totalNutrition.protein)}
                    </div>
                    <div style={{ fontSize: '12px', color: '#595959' }}>белки</div>
                  </div>
                </Col>
                <Col span={6}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#7CB342' }}>
                      {Math.round(totalNutrition.fat)}
                    </div>
                    <div style={{ fontSize: '12px', color: '#595959' }}>жиры</div>
                  </div>
                </Col>
                <Col span={6}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#7CB342' }}>
                      {Math.round(totalNutrition.carbs)}
                    </div>
                    <div style={{ fontSize: '12px', color: '#595959' }}>углеводы</div>
                  </div>
                </Col>
              </Row>
            </Card>
          )}

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              icon={<PlusOutlined />}
              loading={loading}
              size="large"
              style={{ 
                backgroundColor: '#7CB342',
                borderColor: '#7CB342',
                height: '40px',
                borderRadius: '8px'
              }}
            >
              Добавить прием пищи
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Card 
        title={`Последние приемы пищи (${entries.length})`} 
        style={{ borderRadius: '12px' }}
        headStyle={{ fontSize: isMobile ? '16px' : '18px', padding: isMobile ? '12px 16px' : '16px 24px' }}
        className="slide-in"
      >
        <div style={{ overflowX: 'auto' }}>
          {loading ? (
            <SkeletonLoader type="table" rows={5} />
          ) : (
            <Table
              dataSource={entries}
              columns={columns}
              rowKey="id"
              loading={false}
              pagination={{
                pageSize: isMobile ? 5 : 10,
                showSizeChanger: !isMobile,
                showQuickJumper: !isMobile,
                showTotal: (total) => `Всего ${total} записей`,
                simple: isMobile
              }}
              style={{ borderRadius: '8px', overflow: 'hidden' }}
              scroll={isMobile ? { x: 'max-content' } : undefined}
              size={isMobile ? 'small' : 'middle'}
            />
          )}
        </div>
      </Card>

      <Modal
        title={editingEntry ? 'Редактировать прием пищи' : 'Добавить новый прием пищи'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingEntry(null);
          form.resetFields();
          setSelectedFoods([]);
          setTotalNutrition({ calories: 0, protein: 0, fat: 0, carbs: 0 });
          setSearchTerm('');
          setSearchResults([]);
          if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
          }
        }}
        footer={null}
        width={isMobile ? '100%' : 800}
        style={isMobile ? { top: 0, paddingBottom: 0 } : {}}
        bodyStyle={isMobile ? { maxHeight: '100vh', overflowY: 'auto', padding: '16px' } : {}}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Row gutter={16}>
            <Col span={24} md={8}>
              <Form.Item
                label="Прием пищи"
                name="meal_type"
                rules={[{ required: true, message: 'Выберите прием пищи!' }]}
              >
                <Select 
                  placeholder="Выберите прием пищи" 
                  options={mealTypes} 
                  size="large"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label={
              <Space>
                <span>Поиск продуктов</span>
                <Button type="link" size="small" onClick={() => setCustomFoodOpen(true)} style={{ padding: 0 }}>
                  + Свой продукт
                </Button>
              </Space>
            }
            style={{ position: 'relative' }}
          >
            <Input
              ref={searchInputRef}
              placeholder="Например: хлеб, творог, гречка..."
              value={searchTerm}
              onChange={(e) => {
                const value = e.target.value;
                setSearchTerm(value);
                searchFoods(value);
              }}
              onBlur={(e) => {
                // Don't blur if clicking on search results
                const relatedTarget = e.relatedTarget as HTMLElement;
                if (relatedTarget && relatedTarget.closest('.search-results-dropdown')) {
                  e.preventDefault();
                  searchInputRef.current?.focus();
                }
              }}
              size="large"
              suffix={searchTerm && (
                <Button 
                  type="text" 
                  onClick={(e) => {
                    e.stopPropagation();
                    setSearchTerm('');
                    setSearchResults([]);
                    if (searchTimeoutRef.current) {
                      clearTimeout(searchTimeoutRef.current);
                    }
                  }}
                >
                  ✕
                </Button>
              )}
            />
            
            {/* Search results dropdown */}
            {searchResults.length > 0 && (
              <div 
                className="search-results-dropdown"
                style={{ 
                  position: 'absolute', 
                  zIndex: 1000, 
                  width: '100%', 
                  backgroundColor: 'white', 
                  border: '1px solid #d9d9d9', 
                  borderRadius: '6px',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  marginTop: '4px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }}
                onMouseDown={(e) => {
                  // Prevent input blur when clicking on dropdown
                  e.preventDefault();
                }}
              >
                {searchResults.map(food => (
                  <div
                    key={food.id}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f0f0f0',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'white';
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      addFood(food);
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault(); // Prevent input blur
                    }}
                  >
                    <div style={{ fontWeight: 500 }}>{food.display_name || food.name_ru || food.name}</div>
                    <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '2px' }}>
                      {food.category || food.food_category || 'Продукт'} · {food.calories_per_100g} ккал/100г · Б {food.protein_per_100g} / Ж {food.fat_per_100g} / У {food.carbs_per_100g}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Form.Item>

          {/* Selected foods list */}
          {selectedFoods.length > 0 && (
            <Card 
              title="Выбранные продукты" 
              size="small" 
              style={{ marginBottom: 16, borderRadius: '8px' }}
            >
              {selectedFoods.map(food => (
                <div key={food.id} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '8px 0',
                  borderBottom: '1px solid #f0f0f0'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500 }}>{food.display_name || food.name_ru || food.name}</div>
                    <div style={{ fontSize: '12px', color: '#595959' }}>
                      {food.calories_per_100g} ккал/100г
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <InputNumber
                      value={food.quantity}
                      onChange={(value) => updateFoodQuantity(food.id, value ?? 0)}
                      min={0}
                      max={10000}
                      step={10}
                      size="small"
                      style={{ width: 100 }}
                      addonAfter="г"
                      parser={(value) => value!.replace(/\D/g, '')}
                      formatter={(value) => value ? `${value}` : ''}
                    />
                    <Button 
                      size="small" 
                      danger 
                      onClick={() => removeFood(food.id)}
                    >
                      Удалить
                    </Button>
                  </div>
                </div>
              ))}
            </Card>
          )}

          {/* Total nutrition summary */}
          {selectedFoods.length > 0 && (
            <Card 
              title="Итоговая питательная ценность" 
              size="small" 
              style={{ marginBottom: 16, borderRadius: '8px' }}
            >
              <Row gutter={16}>
                <Col span={6}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#7CB342' }}>
                      {Math.round(totalNutrition.calories)}
                    </div>
                    <div style={{ fontSize: '12px', color: '#595959' }}>ккал</div>
                  </div>
                </Col>
                <Col span={6}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#7CB342' }}>
                      {Math.round(totalNutrition.protein)}
                    </div>
                    <div style={{ fontSize: '12px', color: '#595959' }}>белки</div>
                  </div>
                </Col>
                <Col span={6}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#7CB342' }}>
                      {Math.round(totalNutrition.fat)}
                    </div>
                    <div style={{ fontSize: '12px', color: '#595959' }}>жиры</div>
                  </div>
                </Col>
                <Col span={6}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#7CB342' }}>
                      {Math.round(totalNutrition.carbs)}
                    </div>
                    <div style={{ fontSize: '12px', color: '#595959' }}>углеводы</div>
                  </div>
                </Col>
              </Row>
            </Card>
          )}

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                style={{ 
                  backgroundColor: '#7CB342',
                  borderColor: '#7CB342',
                  borderRadius: '8px'
                }}
              >
                {editingEntry ? 'Обновить' : 'Добавить'}
              </Button>
              <Button onClick={() => {
                setModalVisible(false);
                setEditingEntry(null);
                form.resetFields();
                setSelectedFoods([]);
                setTotalNutrition({ calories: 0, protein: 0, fat: 0, carbs: 0 });
              }}>
                Отмена
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Свой продукт"
        open={customFoodOpen}
        onCancel={() => {
          setCustomFoodOpen(false);
          customFoodForm.resetFields();
        }}
        footer={null}
        destroyOnClose
      >
        <Form form={customFoodForm} layout="vertical" onFinish={(v) => submitCustomFood(v, false)}>
          <Form.Item
            name="name"
            label="Название"
            rules={[{ required: true, min: 2, message: 'Укажите название (минимум 2 символа)' }]}
          >
            <Input placeholder="Например: Домашний йогурт" />
          </Form.Item>
          <Form.Item
            name="calories_per_100g"
            label="Ккал на 100 г"
            rules={[{ required: true, message: 'Укажите калорийность' }]}
          >
            <InputNumber min={0} max={900} style={{ width: '100%' }} />
          </Form.Item>
          <Row gutter={8}>
            <Col span={8}>
              <Form.Item name="protein_per_100g" label="Белки">
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="fat_per_100g" label="Жиры">
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="carbs_per_100g" label="Углеводы">
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Button type="primary" htmlType="submit" loading={customFoodLoading} block>
            Сохранить продукт
          </Button>
        </Form>
      </Modal>
    </div>
  );
};
